import { type CapabilityConfig } from '../../../types';
import { hasOnlyKeysFromSet, hasValue } from '../../../utils';

/**
 * Web Permissions to allow the applet iframe.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Permissions_API
 */
export enum Permission {
  ClipboardWrite = 'clipboard-write',

  /**
   * Allow the applet to play sounds straight away.
   *
   * Usually the browser requires the user to interact in the frame before allowing sounds,
   * but we can pass our blessing from host frame (already allowed) to extension frame.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/Media/Autoplay_guide#the_autoplay_permissions_policy
   */
  Autoplay = 'autoplay',

  /**
   * As of Safari 17, Apple Pay in a cross-origin iframe requires the allow="payment" attribute.
   */
  Payment = 'payment',

  /**
   * Allow the applet to use the microphone.
   */
  Microphone = 'microphone',

  /**
   * Allow the applet to use the camera.
   */
  Camera = 'camera',

  // Add more as we need them.
}

const appletPermissionValues = Object.values(Permission) as string[];

export type Applet = {
  frame: HTMLIFrameElement;
  runtimeId: string;
  permissions: Permission[];
};

export const VALID_CONFIG_KEYS = [
  'name',
  'type',
  'url',
  'permissions',
] as const;
export type Config = CapabilityConfig & {
  url: string;
  permissions?: Permission[];
  name?: string;
};

export type AppletPayload = string;

export function isValidConfig(
  config: CapabilityConfig,
  validConfigKeys: readonly string[] = VALID_CONFIG_KEYS,
): config is Config {
  if (!hasOnlyKeysFromSet(config, validConfigKeys)) {
    return false;
  }

  const isTypeNotValid = typeof config.type !== 'string';

  const isUrlNotValid =
    typeof config['url'] !== 'string' || config['url'] === '';

  const isPermissionsNotValid =
    hasValue(config, 'permissions') &&
    (!Array.isArray(config.permissions) ||
      !config.permissions.every(
        (permission) =>
          typeof permission === 'string' &&
          appletPermissionValues.includes(permission),
      ));

  const isNameNotValid =
    hasValue(config, 'name') && typeof config.name !== 'string';

  if (
    isTypeNotValid ||
    isNameNotValid ||
    isUrlNotValid ||
    isPermissionsNotValid
  ) {
    return false;
  }

  return true;
}
